import { useCallback, useRef, useState } from "react";
import { Music, Trash2, Volume2 } from "lucide-react";
import type { TimelineAudioTrack, TimelineAction } from "@scroll/shared-types/timeline";
import VolumeKeyframeEditor from "./VolumeKeyframeEditor";

interface AudioTrackBarProps {
  track: TimelineAudioTrack;
  pixelsPerSecond: number;
  totalDuration: number;
  onDispatch: (action: TimelineAction) => void;
}

export default function AudioTrackBar({
  track,
  pixelsPerSecond,
  totalDuration,
  onDispatch,
}: AudioTrackBarProps) {
  const [showKeyframes, setShowKeyframes] = useState(false);
  const draggingRef = useRef<"left" | "right" | "move" | null>(null);
  const startXRef = useRef(0);
  const startValuesRef = useRef({ timelineStart: 0, trimStart: 0, trimEnd: 0 });
  const lastPatchRef = useRef<Partial<TimelineAudioTrack>>({});

  const trackDuration = track.trimEnd - track.trimStart;
  const left = track.timelineStart * pixelsPerSecond;
  const width = trackDuration * pixelsPerSecond;

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // No immediate blob delete here: deleting the blob now would break
      // undo (the restored track would point at a gone blob and fail the
      // server render on publish). TimelineEditor's dispatch wrapper queues
      // this track's blobKey and releases it on editor close/export if the
      // track was never restored; the daily 03:00 UTC server sweep is the
      // backstop.
      onDispatch({ type: "DELETE_AUDIO_TRACK", id: track.id });
    },
    [track.id, onDispatch],
  );

  const handleMouseDown = useCallback(
    (mode: "left" | "right" | "move", e: React.MouseEvent) => {
      e.stopPropagation();
      draggingRef.current = mode;
      startXRef.current = e.clientX;
      startValuesRef.current = {
        timelineStart: track.timelineStart,
        trimStart: track.trimStart,
        trimEnd: track.trimEnd,
      };
      lastPatchRef.current = {};

      const handleMouseMove = (e: MouseEvent) => {
        const dx = e.clientX - startXRef.current;
        const dt = dx / pixelsPerSecond;
        const s = startValuesRef.current;
        let patch: Partial<TimelineAudioTrack> = {};

        if (draggingRef.current === "move") {
          const newStart = Math.max(0, Math.min(s.timelineStart + dt, totalDuration - (s.trimEnd - s.trimStart)));
          patch = { timelineStart: newStart };
        } else if (draggingRef.current === "left") {
          const newTrimStart = Math.max(0, Math.min(s.trimStart + dt, s.trimEnd - 0.5));
          patch = { trimStart: newTrimStart };
        } else if (draggingRef.current === "right") {
          const newTrimEnd = Math.min(track.sourceDuration, Math.max(s.trimEnd + dt, s.trimStart + 0.5));
          patch = { trimEnd: newTrimEnd };
        }

        lastPatchRef.current = patch;
        onDispatch({ type: "UPDATE_AUDIO_TRACK_PREVIEW", id: track.id, patch });
      };

      const handleMouseUp = () => {
        draggingRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);

        const patch = lastPatchRef.current;
        if (Object.keys(patch).length > 0) {
          // Revert preview, then apply with undo history
          onDispatch({
            type: "UPDATE_AUDIO_TRACK_PREVIEW",
            id: track.id,
            patch: {
              timelineStart: startValuesRef.current.timelineStart,
              trimStart: startValuesRef.current.trimStart,
              trimEnd: startValuesRef.current.trimEnd,
            },
          });
          onDispatch({ type: "UPDATE_AUDIO_TRACK", id: track.id, patch });
        }
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [track, pixelsPerSecond, totalDuration, onDispatch],
  );

  return (
    <div
      className={`timeline-audio-bar${showKeyframes ? " expanded" : ""}`}
      style={{ left: `${left}px`, width: `${width}px` }}
      onMouseDown={(e) => handleMouseDown("move", e)}
    >
      {/* Left trim handle */}
      <div
        className="timeline-audio-handle timeline-audio-handle-left"
        onMouseDown={(e) => handleMouseDown("left", e)}
      />

      {/* Content */}
      <div className="timeline-audio-bar-content">
        <Music size={10} />
        <span className="timeline-audio-bar-title">
          {track.title || "Audio"}
          {track.artist ? ` — ${track.artist}` : ""}
        </span>
        <button
          className="timeline-audio-volume-btn"
          onClick={(e) => {
            e.stopPropagation();
            setShowKeyframes((prev) => !prev);
          }}
          title="Volume keyframes"
          aria-label={showKeyframes ? "Hide volume keyframe editor" : "Show volume keyframe editor"}
          aria-pressed={showKeyframes}
        >
          <Volume2 size={10} aria-hidden="true" />
        </button>
        <button
          className="timeline-audio-delete-btn"
          onClick={handleDelete}
          title="Remove audio"
          aria-label={`Remove ${track.title || "audio track"}`}
        >
          <Trash2 size={10} aria-hidden="true" />
        </button>
      </div>

      {/* Volume keyframe overlay */}
      {showKeyframes && (
        <VolumeKeyframeEditor
          track={track}
          pixelsPerSecond={pixelsPerSecond}
          onDispatch={onDispatch}
        />
      )}

      {/* Right trim handle */}
      <div
        className="timeline-audio-handle timeline-audio-handle-right"
        onMouseDown={(e) => handleMouseDown("right", e)}
      />
    </div>
  );
}
